import { Link, Outlet, useLocation } from 'react-router-dom';
import { Dumbbell, LogIn, UserPlus, Moon, Sun } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useUIStore } from '@/store/uiStore';

/**
 * Лёгкий layout для анонимов: компактный хедер (лого + Sign in / Get started)
 * вместо полноценного Layout (sidebar, user-menu, notifications, WS).
 * <p>
 * Используется PublicOrProtectedLayout для read-only маршрутов
 * (/organizations, /venues, /queues, /), когда пользователь не аутентифицирован.
 * Аноним может просматривать публичные сущности; действия (create/edit/join)
 * недоступны и редиректят на /auth/login.
 */
export function PublicLayout() {
  const location = useLocation();
  const { theme, setTheme } = useUIStore();

  // Apply persisted theme on mount (parity with Layout).
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    }
  }, [theme]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="sticky top-0 z-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2" aria-label="FitManage Home">
            <div className="w-8 h-8 rounded-lg bg-fitness-500 flex items-center justify-center">
              <Dumbbell className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">FitManage</span>
          </Link>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Link to="/auth/login" state={{ from: location }}>
              <Button variant="ghost" size="sm">
                <LogIn className="h-4 w-4 mr-2" />
                Войти
              </Button>
            </Link>
            <Link to="/auth/register" state={{ from: location }}>
              <Button size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Регистрация
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6 lg:p-8" id="main-content">
        <Outlet />
      </main>
    </div>
  );
}
